// Compressão de imagens no browser, ANTES do upload — sem dependências externas
// (usa canvas nativo). Reduz fotos de telemóvel (5-15 MB) para ~300-600 KB, mantendo
// boa qualidade para ecrã/lightbox. Poupa Storage, acelera uploads e os transforms.
//
// Estratégia: redimensiona para um lado máximo de ~2000px e recodifica em WebP q0.82.
// Devolve o ORIGINAL inalterado quando comprimir não ajuda ou não é seguro:
//   - GIFs (animação perder-se-ia ao passar por canvas);
//   - formatos não-imagem ou que o canvas não consiga ler;
//   - quando o resultado ficaria maior do que o original.

const LADO_MAX = 2000;
const QUALIDADE = 0.82;
const TIPO_SAIDA = "image/webp";

/** Troca a extensão do nome do ficheiro (ex.: foto.jpg → foto.webp). */
function comExtensao(nome: string, ext: string): string {
  const base = nome.replace(/\.[^.]+$/, "");
  return `${base}.${ext}`;
}

/**
 * Comprime uma imagem no browser. Nunca rejeita: em caso de dúvida devolve o
 * ficheiro original, para o upload continuar a funcionar.
 */
export async function comprimirImagem(ficheiro: File): Promise<File> {
  // GIF: não passar por canvas (perderia a animação).
  if (ficheiro.type === "image/gif") return ficheiro;
  // Só tentamos comprimir imagens.
  if (!ficheiro.type.startsWith("image/")) return ficheiro;

  try {
    const bitmap = await createImageBitmap(ficheiro);

    const { width, height } = bitmap;
    const maior = Math.max(width, height);
    const escala = maior > LADO_MAX ? LADO_MAX / maior : 1;
    const novaLargura = Math.round(width * escala);
    const novaAltura = Math.round(height * escala);

    const canvas = document.createElement("canvas");
    canvas.width = novaLargura;
    canvas.height = novaAltura;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close();
      return ficheiro;
    }
    ctx.drawImage(bitmap, 0, 0, novaLargura, novaAltura);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, TIPO_SAIDA, QUALIDADE),
    );

    // toBlob falhou ou WebP não suportado, ou ficou maior → fica o original.
    if (!blob || blob.type !== TIPO_SAIDA || blob.size >= ficheiro.size) {
      return ficheiro;
    }

    return new File([blob], comExtensao(ficheiro.name, "webp"), {
      type: TIPO_SAIDA,
      lastModified: ficheiro.lastModified,
    });
  } catch {
    // Qualquer erro de descodificação → devolve o original (não bloqueia o upload).
    return ficheiro;
  }
}
